// Theme manager — toggles `data-theme` on <html>. Persists choice in
// localStorage; falls back to system preference for first-time visitors.

import { useEffect, useSyncExternalStore } from 'react';

export type Theme = 'dark' | 'light';
const KEY = 'okn:theme';

function readSystem(): Theme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
}

function currentTheme(): Theme {
  return readStored() ?? readSystem();
}

function apply(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

const listeners = new Set<() => void>();
function notify() {
  for (const l of listeners) l();
}

export function setTheme(theme: Theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* ignore */
  }
  apply(theme);
  notify();
}

export function toggleTheme() {
  setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
}

export function initTheme() {
  apply(currentTheme());
  // React to OS-level changes ONLY when the user hasn't explicitly chosen.
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  mq.addEventListener('change', () => {
    if (readStored() === null) {
      apply(readSystem());
      notify();
    }
  });
}

export function useTheme(): Theme {
  const get = () => currentTheme();
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    get,
    get,
  );
}

// Sugar for callers that just want to ensure init happened on mount.
export function useThemeBoot() {
  useEffect(() => {
    initTheme();
  }, []);
}
